import { useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from "@react-oauth/google";
import api from "../api";

export default function Register() {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponDetails, setCouponDetails] = useState(null); // { type, value, code }
  const [couponMsg, setCouponMsg] = useState("");

  const [msg, setMsg] = useState("");
  const [toastMsg, setToastMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch Plans on Mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get('/api/settings/public');
        if (res.data.success && res.data.data.plans) {
          setPlans(res.data.data.plans);
        }
      } catch (err) {
        console.error("Failed to load plans", err);
      }
    };
    fetchPlans();
  }, []);

  const verifyCoupon = async () => {
    if (!couponCode) return;
    try {
      const res = await api.post('/api/settings/verify-coupon', { code: couponCode });
      if (res.data.valid) {
        setCouponDetails({
          type: res.data.discountType,
          value: res.data.discountValue,
          maxDiscount: res.data.maxDiscount, // Add this
          code: res.data.code
        });
        setCouponMsg("Coupon applied successfully!");
      } else {
        setCouponDetails(null);
        setCouponMsg(res.data.message || "Invalid coupon");
      }
    } catch (err) {
      setCouponDetails(null);
      setCouponMsg("Error verifying coupon");
    }
  };

  const getPriceDetails = () => {
    if (!selectedPlan) return { total: 0 };

    let basePrice = Number(selectedPlan.price);
    let discount = 0;

    if (couponDetails) {
      if (couponDetails.type === 'percent') {
        discount = (basePrice * couponDetails.value) / 100;
        // Cap discount if maxDiscount is set
        if (couponDetails.maxDiscount && discount > couponDetails.maxDiscount) {
          discount = couponDetails.maxDiscount;
        }
      } else {
        discount = couponDetails.value;
      }
    }

    let netPrice = Math.max(0, basePrice - discount);

    // Taxes
    const cgst = netPrice * 0.09;
    const igst = netPrice * 0.09;
    const total = netPrice + cgst + igst;

    return {
      basePrice,
      discount,
      netPrice,
      cgst,
      igst,
      total
    };
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setMsg("");
    setToastMsg("");

    if (step === 1) {
      // 1. Validate Basic Info
      if (!username || !email || !password) {
        setMsg("Please fill all fields");
        setToastMsg("Please fill all fields");
        setTimeout(() => setToastMsg(""), 3000);
        return;
      }

      // 2. Simplified Password Check
      // Min 6 chars, text and number only (no special characters)
      const passwordRegex = /^[a-zA-Z0-9]{6,}$/;
      if (!passwordRegex.test(password)) {
        const pwdError = "Password must be at least 6 characters (letters and numbers only).";
        setMsg(pwdError);
        setToastMsg(pwdError);
        setTimeout(() => setToastMsg(""), 5000); // Longer timeout for complex message
        return;
      }

      // 3. Check Email Availability
      setLoading(true);
      try {
        const res = await api.post("/api/auth/check-email", { email });

        // STRICT VALIDATION: Ensure we got a real JSON response, not a fallback HTML page
        if (typeof res.data === 'string' && res.data.trim().startsWith('<!DOCTYPE html>')) {
          setMsg("Server HTML Error: Backend may be down");
          setToastMsg("Server routing error. Please try again.");
          setTimeout(() => setToastMsg(""), 3000);
          setLoading(false);
          return;
        }

        if (res.data && res.data.available === false) {
          setMsg("Email already registered");
          setToastMsg("Email already registered");
          setTimeout(() => setToastMsg(""), 3000);
          setLoading(false);
          return;
        }

        // If all good, proceed
        setStep(2);
      } catch (err) {
        setMsg("Error validating email");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Step 2 Submission
    if (!selectedPlan) {
      setMsg("Please select a plan");
      return;
    }

    setLoading(true);
    const { total } = getPriceDetails();

    try {
      const res = await api.post("/api/auth/register", {
        username,
        email,
        password,
        plan_id: selectedPlan.id,
        amount_paid: total.toFixed(2),
        coupon_code: couponDetails ? couponDetails.code : null
      });

      if (res.data.user) {
        sessionStorage.setItem("authToken", "session-active"); // Or res.data.token if backend returns it
        if (res.data.token) sessionStorage.setItem("authToken", res.data.token);
        sessionStorage.setItem("user", JSON.stringify(res.data.user));

        setMsg("✅ Registration Successful! Redirecting...");
        setTimeout(() => navigate("/"), 1500);
      }
    } catch (err) {
      const errorText = err.response?.data?.message || "Registration failed";
      setMsg(errorText);
      setToastMsg(errorText);
      // Auto-hide toast after 3 seconds
      setTimeout(() => setToastMsg(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const priceDetails = getPriceDetails();

  return (
    <div className="min-vh-100 d-flex align-items-center bg-light py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow-lg border-0 overflow-hidden">
              <div className="row g-0">
                {/* Left Side / Top Banner - Progress */}
                <div className="col-12 bg-success text-white p-3 d-flex justify-content-between align-items-center">
                  <h4 className="mb-0"><i className="las la-user-plus me-2"></i>Register</h4>
                  <div className="d-flex align-items-center">
                    <span className={`badge rounded-pill ${step === 1 ? 'bg-white text-success' : 'bg-success border'}`}>1. Details</span>
                    <div className="mx-2 border-top w-[20px]"></div>
                    <span className={`badge rounded-pill ${step === 2 ? 'bg-white text-success' : 'bg-success border'}`}>2. Plan & Billing</span>
                  </div>
                </div>

                <div className="col-12 p-4 p-md-5">
                  {/* Removed Top Alert */}

                  <form onSubmit={handleRegister}>
                    {/* STEP 1: User Details */}
                    {step === 1 && (
                      <div className="animation-fade-in">
                        <h5 className="mb-4 text-muted">Create your account</h5>

                        <div className="mb-3">
                          <label className="form-label">Full Name</label>
                          <input
                            type="text"
                            className="form-control form-control-lg"
                            placeholder="Enter your name"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required={step === 1}
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Email Address</label>
                          <input
                            type="email"
                            className="form-control form-control-lg"
                            placeholder="name@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="off"
                            required={step === 1}
                          />
                        </div>

                        <div className="mb-4">
                          <label className="form-label">Password</label>
                          <div className="input-group">
                            <input
                              type={showPassword ? "text" : "password"}
                              className="form-control form-control-lg"
                              placeholder="Create a strong password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              autoComplete="new-password"
                              required={step === 1}
                            />
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              <i className={showPassword ? "las la-eye-slash" : "las la-eye"}></i>
                            </button>
                          </div>
                        </div>

                        <button type="submit" className="btn btn-success btn-lg w-100" disabled={loading}>
                          {loading ? <i className="las la-spinner la-spin"></i> : <><i className="las la-check-circle me-2"></i> Next: Select Plan <i className="las la-arrow-right ms-2"></i></>}
                        </button>

                        {/* Inline Error below button for Step 1 */}
                        {msg && step === 1 && <div className="text-danger text-center mt-2 fw-bold small">{msg}</div>}

                        <div className="text-center mt-3">
                          <div className="d-flex align-items-center my-3">
                            <hr className="flex-grow-1" />
                            <span className="mx-2 text-muted small">OR REGISTER WITH</span>
                            <hr className="flex-grow-1" />
                          </div>
                          <div className="d-flex justify-content-center">
                            <div className="max-w-[300px]">
                              <GoogleLogin
                                onSuccess={async (res) => {
                                  /* Google Logic similar to before, maybe simpler to just redirect/auto-register */
                                  /* For MVP, let's keep existing google logic or just console log it as we focused on plans */
                                  /* Re-implementing simplified google auth handling */
                                  try {
                                    const { credential } = res;
                                    setMsg("Processing Google Sign-In...");
                                    const apiRes = await api.post("/api/auth/google", { token: credential });
                                    if (apiRes.data.user) {
                                      sessionStorage.setItem("authToken", apiRes.data.token);
                                      sessionStorage.setItem("user", JSON.stringify(apiRes.data.user));
                                      navigate("/");
                                    }
                                  } catch (e) { console.error(e); setMsg("Google Login Failed"); }
                                }}
                                onError={() => setMsg("Google Login Failed")}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="text-center mt-4">
                          <Link to="/login" className="text-decoration-none">Already have an account? Login</Link>
                        </div>
                      </div>
                    )}

                    {/* STEP 2: Plans & Billing */}
                    {step === 2 && (
                      <div className="animation-fade-in">
                        <button type="button" className="btn btn-link text-decoration-none p-0 mb-3" onClick={() => setStep(1)}>
                          <i className="las la-arrow-left me-1"></i> Back to Details
                        </button>

                        <h5 className="mb-4 text-muted">Select a Plan</h5>

                        <div className="row g-3 mb-4">
                          {plans.length > 0 ? plans.map(plan => (
                            <div className="col-md-6" key={plan.id}>
                              <div
                                className={`card h-100 cursor-pointer transition-all ${selectedPlan?.id === plan.id ? 'border-success bg-success bg-opacity-10 shadow' : 'border-light shadow-sm'}`}
                                onClick={() => setSelectedPlan(plan)}
                                 
                              >
                                <div className="card-body">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="card-title mb-0 fw-bold">{plan.name}</h6>
                                    {selectedPlan?.id === plan.id && <i className="las la-check-circle text-success fs-4"></i>}
                                  </div>
                                  <h3 className="text-success mb-3">${plan.price}<small className="text-muted fs-6">/{plan.interval}</small></h3>
                                  <ul className="list-unstyled small mb-0">
                                    {plan.features && plan.features.map((f, i) => (
                                      <li key={i} className="mb-1"><i className="las la-check text-success me-1"></i> {f}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          )) : (
                            <div className="col-12"><p>Loading plans...</p></div>
                          )}
                        </div>

                        {selectedPlan && (
                          <div className="card bg-light border-0 mb-4">
                            <div className="card-body">
                              <h6 className="fw-bold mb-3">Billing Summary</h6>

                              {/* Coupon Section */}
                              <div className="input-group mb-3">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Have a coupon?"
                                  value={couponCode}
                                  onChange={(e) => setCouponCode(e.target.value)}
                                />
                                <button className="btn btn-outline-secondary" type="button" onClick={verifyCoupon}>Apply</button>
                              </div>
                              {couponMsg && <p className={`small ${couponDetails ? 'text-success' : 'text-danger'} mb-2`}>{couponMsg}</p>}

                              {/* Breakdown */}
                              <div className="d-flex justify-content-between mb-1 small">
                                <span>Base Price</span>
                                <span>${priceDetails.basePrice.toFixed(2)}</span>
                              </div>
                              {priceDetails.discount > 0 && (
                                <div className="d-flex justify-content-between mb-1 small text-success">
                                  <span>Discount ({couponDetails.code})</span>
                                  <span>-${priceDetails.discount.toFixed(2)}</span>
                                </div>
                              )}
                              <hr className="my-2" />
                              <div className="d-flex justify-content-between mb-1 small">
                                <span>Net Price</span>
                                <span>${priceDetails.netPrice.toFixed(2)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-1 small text-muted">
                                <span>CGST (9%)</span>
                                <span>+${priceDetails.cgst.toFixed(2)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-2 small text-muted">
                                <span>IGST (9%)</span>
                                <span>+${priceDetails.igst.toFixed(2)}</span>
                              </div>
                              <div className="d-flex justify-content-between fw-bold fs-5 border-top pt-2">
                                <span>Total Payable</span>
                                <span>${priceDetails.total.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <button
                          type="submit"
                          className="btn btn-success btn-lg w-100"
                          disabled={!selectedPlan || loading}
                        >
                          {loading ? <i className="las la-spinner la-spin"></i> : <><i className="las la-check-circle me-2"></i> Confirm & Register</>}
                        </button>

                        {/* Inline Error below button */}
                        {msg && <div className="text-danger text-center mt-2 fw-bold small">{msg}</div>}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toastMsg && (
        <div className="position-fixed top-0 end-0 p-3 z-[1050]">
          <div className="toast show align-items-center text-white bg-danger border-0" role="alert" aria-live="assertive" aria-atomic="true">
            <div className="d-flex">
              <div className="toast-body">
                {toastMsg}
              </div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToastMsg("")} aria-label="Close"></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
